import { memo, useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { MessageCircle, Loader2, X } from "lucide-react";
import { cn } from "@/lib/utils";
import {
  Dialog,
  DialogContent,
  DialogTrigger,
  DialogTitle,
} from "@/components/ui/dialog";
import * as VisuallyHidden from "@radix-ui/react-visually-hidden";

interface AIChatDialogProps {
  trigger?: React.ReactNode;
  className?: string;
  context?: string; // Kept for interface compatibility
}

export const AIChatDialog = memo(function AIChatDialog({
  trigger,
  className,
}: AIChatDialogProps) {
  const [isOpen, setIsOpen] = useState(false);
  const [conversationToken, setConversationToken] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [isLoadingToken, setIsLoadingToken] = useState(false);

  useEffect(() => {
    if (isOpen && !conversationToken && !isLoadingToken) {
      const fetchToken = async () => {
        setIsLoadingToken(true);
        setError(null);
        try {
          const response = await fetch(import.meta.env.VITE_SUPABASE_FUNCTION_URL, {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
              Authorization: `Bearer ${import.meta.env.VITE_SUPABASE_PUBLISHABLE_KEY}`,
            },
            body: JSON.stringify({ variant: "signed_url" }),
          });
          const data = await response.json();
          if (data.signed_url) {
            setConversationToken(data.signed_url);
          } else {
            console.error("No token received", data);
            setError("Failed to initialize chat");
          }
        } catch (err) {
          console.error("Token fetch error:", err);
          setError("Connection failed");
        } finally {
          setIsLoadingToken(false);
        }
      };
      
      fetchToken();

      // Load Script (Lazy Loading)
      if (!document.querySelector('script[src*="convai-widget-embed"]')) {
        const script = document.createElement("script");
        script.src = "https://unpkg.com/@elevenlabs/convai-widget-embed";
        script.async = true;
        script.type = "text/javascript";
        document.body.appendChild(script);
      }
    }
  }, [isOpen, conversationToken, isLoadingToken]);

  return (
    <Dialog open={isOpen} onOpenChange={setIsOpen}>
      <DialogTrigger asChild>
        <div 
          className="cursor-pointer" 
          role="button" 
          tabIndex={0}
          onKeyDown={(e) => {
            if (e.key === 'Enter' || e.key === ' ') {
              e.preventDefault();
              setIsOpen(true);
            }
          }}
        >
          {trigger || (
            <Button 
              variant="outline" 
              className={cn("rounded-full gap-2 text-sm", className)}
            >
              <MessageCircle className="w-4 h-4" />
              <span>Chat Now</span>
            </Button>
          )}
        </div>
      </DialogTrigger>
      <DialogContent 
        className="max-w-[450px] w-[95vw] h-[800px] max-h-[90vh] p-0 overflow-visible border-none bg-transparent shadow-none [&>button]:hidden flex items-center justify-center"
      >
        <VisuallyHidden.Root>
          <DialogTitle>AI Assistant Chat</DialogTitle>
        </VisuallyHidden.Root>

        <div className="relative w-full h-full">
          {/* Custom Close Button - Positioned exactly at the card corner */}
          <div className="absolute top-8 right-4 z-[110]">
            <Button
              variant="ghost"
              size="icon"
              className="h-8 w-8 rounded-full bg-black/40 hover:bg-black/60 text-white transition-all backdrop-blur-md border border-white/10"
              onClick={() => setIsOpen(false)}
            >
              <X className="h-4 w-4" />
              <span className="sr-only">Close Chat</span>
            </Button>
          </div>

          <div className="w-full h-full relative z-10 overflow-hidden">
            {conversationToken ? (
              <elevenlabs-convai 
                agent-id={import.meta.env.VITE_ELEVENLABS_AGENT_ID} 
                signed-url={conversationToken}
                style={{ 
                  width: "110%",
                  height: "110%",
                  position: "absolute",
                  left: "50%",
                  top: "50%",
                  transform: "translate(-50%, -50%)",
                }}
              ></elevenlabs-convai>
            ) : (
              <div className="flex flex-col items-center justify-center h-full text-white gap-4 bg-black/40 backdrop-blur-3xl">
                {error ? (
                  <div className="text-destructive font-medium px-6 text-center">{error}</div>
                ) : (
                  <>
                    <div className="relative">
                      <Loader2 className="h-10 w-10 animate-spin text-accent" />
                      <div className="absolute inset-0 blur-lg bg-accent/20 animate-pulse" />
                    </div>
                    <p className="text-sm font-medium tracking-wide">Connecting to Dr. Maya...</p>
                  </>
                )}
              </div>
            )}
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
});
